import { Component } from '@verndale/core';
import { animate, ElementOrSelector, stagger, timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { EventBusSingleton } from '../../../animation/eventBus';

class DesktopGlobalNavigation extends Component {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
  MOTION_ZERO_DURATION = 0.000001;
  diffLevel1 = 0;
  diffLevel2 = 0;
  activeMenu: HTMLElement | null = null;
  currentFeaturedItem: HTMLElement | null = null;
  isDesktop = false;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  initialDelay = 0.75;
  hasMastheadPlayed: string | null;

  constructor(el: HTMLElement) {
    super(el);
    this.hasMastheadPlayed = localStorage.getItem(
      'manhattan_masthead-animation'
    );
    if (this.hasMastheadPlayed === null) {
      if (!this.reducedMotion.matches) {
        if (!document.querySelector('.intro-animation-overlay')) {
          this.initialDelay = 0;
          this.animateIn();
        } else {
          EventBusSingleton.subscribe(
            'introAnimationOverlay:hide',
            this.animateIn.bind(this)
          );
        }
      }
    }
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      menuTriggers: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-trigger'
      ),
      cta: this.el.querySelector<HTMLElement>('.global-navigation__cta'),
      container: this.el.querySelector<HTMLElement>(
        '.global-navigation__container'
      ),
      navigationItems: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__item'
      ),
      menus: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu'),
      level3Triggers: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-level-3-trigger'
      ),
      level3Menus: this.el.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-level-3-wrapper'
      ),
      background: this.el.querySelector<HTMLElement>(
        '.global-navigation__background'
      ),
      logo: this.el.querySelector<HTMLElement>('.global-navigation__logo')
    };
    this.listenerRefs = {
      toggleMenu: this.toggleMenu.bind(this),
      toggleLevel3Menu: this.toggleLevel3Menu.bind(this),
      handleClickOutside: this.handleClickOutside.bind(this)
    };
  }

  addListeners() {
    [].forEach.call(this.dom.menuTriggers, (trigger: HTMLElement) => {
      trigger.addEventListener('click', this.listenerRefs.toggleMenu);
    });
    [].forEach.call(this.dom.level3Triggers, (trigger: HTMLElement) => {
      trigger.addEventListener('click', this.listenerRefs.toggleLevel3Menu);
    });
    document.addEventListener(
      'click',
      this.listenerRefs.handleClickOutside,
      true
    );
  }

  removeListeners() {
    [].forEach.call(this.dom.menuTriggers, (trigger: HTMLElement) => {
      trigger.removeEventListener('click', this.listenerRefs.toggleMenu);
    });
    [].forEach.call(this.dom.level3Triggers, (trigger: HTMLElement) => {
      trigger.removeEventListener('click', this.listenerRefs.toggleLevel3Menu);
    });
    document.removeEventListener(
      'click',
      this.listenerRefs.handleClickOutside,
      true
    );
  }

  handleClickOutside(e: Event) {
    if (!this.el.contains(e.target as Node)) {
      this.hideMenu();
    }
  }

  animateIn() {
    (this.dom.container as HTMLElement).style.overflow = 'hidden';
    const staggerItems = [] as HTMLElement[];
    staggerItems.push(this.dom.logo as HTMLElement);
    staggerItems.push(...(this.dom.navigationItems as NodeListOf<HTMLElement>));

    (this.dom.background as HTMLElement).style.zIndex = '999';
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push({ name: 'z-index', at: 1 });
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        x: [`${window.innerWidth}px`, 0]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1.25,
        at: 'start'
      }
    ]);
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        scaleX: [0, 1]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1.2,
        at: 'start',
        easing: 'linear'
      }
    ]);
    sequence.push([
      this.dom.background as ElementOrSelector,
      {
        zIndex: -1
      },
      {
        duration: this.MOTION_ZERO_DURATION,
        at: 'z-index',
        easing: 'linear'
      }
    ]);
    sequence.push([
      staggerItems.reverse(),
      {
        opacity: [0, 1],
        transform: ['translateY(60px)', 'translateY(0)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        delay: this.reducedMotion.matches
          ? this.MOTION_ZERO_DURATION
          : stagger(0.05),
        opacity: {
          duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 1
        }
      }
    ]);
    timeline(sequence, { delay: this.initialDelay }).finished.then(() => {
      (this.dom.container as HTMLElement).style.overflow = '';
      staggerItems.forEach(item => {
        item.style.opacity = '';
        item.style.transform = '';
      });
    });
    this.animateCTAClipPath();
  }

  animateCTAClipPath() {
    if (this.dom.cta)
      animate(
        progress => {
          (this.dom.cta as HTMLElement).setAttribute(
            'style',
            `--clip:  calc(${100 - progress * 100}% - ${progress * 20}px)`
          );
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 1.25,
          easing: 'linear',
          delay: this.reducedMotion.matches ? 0 : this.initialDelay + 0.25
        }
      );
  }

  async toggleMenu(e: Event) {
    const trigger = e.currentTarget as HTMLElement;
    if (trigger.getAttribute('aria-expanded') === 'false') {
      const menuTarget = trigger.getAttribute('aria-controls');
      if (!menuTarget) return;
      const menu = document.getElementById(menuTarget) as HTMLElement;
      await this.hideMenu();
      this.showMenu(menu, trigger);
    } else {
      this.hideMenu();
    }
  }

  hideLevel3Menus() {
    [].forEach.call(this.dom.level3Triggers, (trigger: HTMLElement) => {
      const menuTarget = trigger.getAttribute('aria-controls');
      if (!menuTarget) return;
      const menu = document.getElementById(menuTarget) as HTMLElement;
      (menu as HTMLElement).style.display = 'none';
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  showMenu(menu: HTMLElement, trigger: HTMLElement) {
    menu.style.display = 'flex';
    requestAnimationFrame(async () => {
      let diff = this.getWidthDifference(menu);
      if (diff > 0) diff = 0;
      this.diffLevel1 = diff;
      const sequence = [] as TimelineDefinition;
      sequence.push({ name: 'start', at: 0 });
      sequence.push({ name: 'links-enter', at: 0.25 });
      sequence.push([
        menu as ElementOrSelector,
        {
          opacity: [0, 1],
          transform: `translate(${this.diffLevel1}px, 0)`
        },
        {
          duration: this.MOTION_ZERO_DURATION,
          at: 'start'
        }
      ]);
      sequence.push([
        menu.querySelector<HTMLElement>(
          '.global-navigation__menu-inner'
        ) as ElementOrSelector,
        {
          transform: ['translate(0, -100%)', 'translate(0, 0)']
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          at: 'start'
        }
      ]);
      sequence.push([
        menu.querySelectorAll<HTMLElement>(
          '.global-navigation__menu-left > .link'
        ),
        {
          transform: ['translate(-25px, 0)', 'translate(0, 0)'],
          opacity: [0, 1]
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.25,
          delay: this.reducedMotion.matches ? 0 : stagger(0.1),
          at: this.reducedMotion.matches ? 'start' : 'links-enter'
        }
      ]);
      trigger.setAttribute('aria-expanded', 'true');
      this.activeMenu = menu;
      this.animateFeaturedItemClipPathIn();
      this.animateMenuClipPathsIn(menu);
      timeline(sequence);
    });
  }

  async hideMenu() {
    const menu = this.activeMenu as HTMLElement;
    if (!menu) return;
    const menuInner = menu.querySelector<HTMLElement>(
      '.global-navigation__menu-inner'
    ) as HTMLElement;
    if (!menuInner) return;
    const links = Array.from(
      menu.querySelectorAll<HTMLElement>(
        '.global-navigation__menu-left > .link'
      )
    );
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push({ name: 'inner-up', at: 0.25 });
    sequence.push([
      links.reverse() as ElementOrSelector,
      {
        transform: 'translate(-25px, 0)',
        opacity: [1, 0]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.15,
        delay: this.reducedMotion.matches ? 0 : stagger(0.05),
        at: 'start'
      }
    ]);
    sequence.push([
      menuInner,
      {
        transform: ['translate(0, 0)', 'translate(0, -100%)']
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25,
        at: this.reducedMotion.matches ? 'start' : 'inner-up'
      }
    ]);
    this.animateMenuClipPathsOut(menu);
    await timeline(sequence).finished.then(() => {
      menuInner.removeAttribute('style');
      this.activeMenu?.removeAttribute('style');
      this.activeMenu = null;
      this.hideLevel3Menus();
      [].forEach.call(this.dom.menuTriggers, (trigger: HTMLElement) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  async toggleLevel3Menu(e: Event) {
    const trigger = e.currentTarget as HTMLElement;
    const menuTarget = trigger.getAttribute('aria-controls');
    if (!menuTarget) return;
    const menu = document.getElementById(menuTarget) as HTMLElement;
    if (trigger.getAttribute('aria-expanded') === 'false') {
      await this.hideLevel3ActiveMenu(trigger);
      trigger.setAttribute('aria-expanded', 'true');
      await this.animateFeaturedItemClipPathOut();
      this.showLevel3Menu(menu);
    } else {
      if (this.currentFeaturedItem) {
        await this.hideLevel3Menu(menu, trigger);
        await this.animateFeaturedItemClipPathIn();
      } else {
        this.hideLevel3Menu(menu, trigger);
        this.animateFeaturedItemClipPathIn();
      }
    }
  }

  async hideLevel3ActiveMenu(trigger: HTMLElement) {
    const activeTrigger = (
      trigger as HTMLElement
    ).parentElement?.querySelector<HTMLElement>(
      '.global-navigation__menu-level-3-trigger[aria-expanded="true"]'
    );

    if (!activeTrigger) return;
    const activeMenuTarget = activeTrigger.getAttribute('aria-controls');
    if (!activeMenuTarget) return;
    const activeMenu = document.getElementById(activeMenuTarget) as HTMLElement;
    await this.hideLevel3Menu(activeMenu, activeTrigger);
  }

  showLevel3Menu(menu: HTMLElement) {
    if (!this.activeMenu) return;
    (menu as HTMLElement).style.display = 'flex';
    requestAnimationFrame(() => {
      let diff = this.getWidthDifference(this.activeMenu as HTMLElement);
      if (diff > 0) diff = 0;
      this.diffLevel2 = diff;
      const items = menu.querySelectorAll<HTMLElement>('.link');
      const sequence = [] as TimelineDefinition;
      sequence.push({ name: 'start', at: 0 });
      sequence.push([
        items as ElementOrSelector,
        {
          transform: ['translate(-25px, 0)', 'translate(0, 0)'],
          opacity: [0, 1]
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          delay: this.reducedMotion.matches ? 0 : stagger(0.1),
          at: 'start'
        }
      ]);
      sequence.push([
        (this.activeMenu as HTMLElement).querySelector(
          '.global-navigation__menu-inner'
        ) as ElementOrSelector,
        {
          clipPath: [
            'inset(-50px calc(100% - 330px) -50px -50px)',
            'inset(-50px -50px -50px -50px)'
          ]
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          at: 'start'
        }
      ]);
      sequence.push([
        this.activeMenu as ElementOrSelector,
        {
          transform: `translate(${this.diffLevel2 + this.diffLevel1}px, 0)`
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.25,
          at: 'start'
        }
      ]);
      timeline(sequence);
    });
  }

  async hideLevel3Menu(menu: HTMLElement, trigger: HTMLElement) {
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      (this.activeMenu as HTMLElement).querySelector(
        '.global-navigation__menu-inner'
      ) as ElementOrSelector,
      {
        clipPath: [
          'inset(-50px -50px -50px -50px)',
          'inset(-50px calc(100% - 330px) -50px -50px)'
        ]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'start'
      }
    ]);
    sequence.push([
      this.activeMenu as ElementOrSelector,
      {
        transform: `translate(${this.diffLevel1}px, 0)`
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'start'
      }
    ]);
    await timeline(sequence).finished.then(() => {
      (menu as HTMLElement).style.display = 'none';
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  async animateFeaturedItemClipPathIn() {
    this.currentFeaturedItem = (this.activeMenu as HTMLElement).querySelector(
      '.global-navigation__menu-right .featured-menu-item'
    );
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      (this.activeMenu as HTMLElement).querySelector(
        '.global-navigation__menu-inner'
      ) as ElementOrSelector,
      {
        clipPath: 'inset(-50px -50px -50px -50px)'
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'start'
      }
    ]);
    timeline(sequence);

    if (!this.currentFeaturedItem) return;
    (this.currentFeaturedItem as HTMLElement).style.display = 'block';
    await animate(
      progress => {
        (this.currentFeaturedItem as HTMLElement).style.setProperty(
          '--featured-menu-item-clip',
          `${100 - progress * 100}%`
        );
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5
      }
    ).finished.then();
  }

  async animateFeaturedItemClipPathOut() {
    this.currentFeaturedItem = (this.activeMenu as HTMLElement).querySelector(
      '.global-navigation__menu-right .featured-menu-item'
    );
    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    sequence.push([
      (this.activeMenu as HTMLElement).querySelector(
        '.global-navigation__menu-inner'
      ) as ElementOrSelector,
      {
        clipPath: [
          'inset(-50px -50px -50px -50px)',
          'inset(-50px calc(100% - 330px) -50px -50px)'
        ]
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        at: 'start'
      }
    ]);
    timeline(sequence);

    if (!this.currentFeaturedItem) return;

    await animate(
      progress => {
        (this.currentFeaturedItem as HTMLElement).style.setProperty(
          '--featured-menu-item-clip',
          `${progress * 100}%`
        );
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5
      }
    ).finished.then(() => {
      (this.currentFeaturedItem as HTMLElement).style.display = 'none';
    });
  }

  animateMenuClipPathsIn(menu: HTMLElement) {
    animate(
      progress => {
        (menu as HTMLElement).style.setProperty(
          '--menu-clip',
          `calc(${100 - progress * 100}% - 25px)`
        );
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5,
        easing: 'ease-out'
      }
    );
    animate(
      progress => {
        (menu as HTMLElement)
          .querySelector<HTMLElement>(
            '.global-navigation__menu-right .featured-menu-item'
          )
          ?.style.setProperty(
            '--featured-menu-item-clip',
            `${99.75 - progress * 99.75}%`
          );
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.75,
        delay: this.reducedMotion.matches ? 0 : 0.25
      }
    );
  }

  animateMenuClipPathsOut(menu: HTMLElement) {
    animate(
      progress => {
        (menu as HTMLElement).style.setProperty(
          '--menu-clip',
          `calc(${progress * 100}% + ${progress * 25}px)`
        );
      },
      {
        easing: 'ease-out',
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.15,
        delay: this.reducedMotion.matches ? 0 : 0.25
      }
    );
    animate(
      progress => {
        (menu as HTMLElement)
          .querySelector<HTMLElement>(
            '.global-navigation__menu-right .featured-menu-item'
          )
          ?.style.setProperty(
            '--featured-menu-item-clip',
            `${progress * 99.75}%`
          );
      },
      {
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.25
      }
    );
  }

  getWidthDifference(menu: HTMLElement) {
    const featuredItem = (menu as HTMLElement).querySelector(
      '.global-navigation__menu-right .featured-menu-item'
    );
    if (featuredItem) (featuredItem as HTMLElement).style.display = 'block';

    const header = document.querySelector('.header') as HTMLElement;
    const menuRight = menu.getBoundingClientRect().right;
    const navRight = header.getBoundingClientRect().right;
    if (featuredItem) (featuredItem as HTMLElement).style.display = 'none';

    return navRight - menuRight;
  }
}

export default DesktopGlobalNavigation;
